import { Box, Button, Link, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SigninLogo } from "../../components/SigninLogo/SigninLogo";
import { INK, INK_BLACK, NEUTRAL, SURFACE } from "../../theme/tokens";
import { APP_STORE_URL, PLAY_STORE_URL } from "../../utils/models";

export const Welcome: FC = () => {
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const theme = useTheme();
	const BRAND_PRIMARY = theme.palette.primary.main;
	const BRAND_SECONDARY = INK.SECONDARY;

	const token = searchParams.get("token") ?? "";
	const firstName = searchParams.get("firstName") ?? "";
	const facilityName = searchParams.get("facilityName") ?? "";

	return (
		<Box
			sx={{
				width: "100%",
				minHeight: "100vh",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				bgcolor: NEUTRAL[100],
				overflowY: "auto",
			}}
		>
			{/* Header */}
			<Box
				sx={{
					width: "100%",
					bgcolor: BRAND_PRIMARY,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					pt: { xs: 2, sm: 4 },
					pb: { xs: 3, sm: 5 },
					gap: { xs: 1, sm: 0.5 },
				}}
			>
				<SigninLogo onDark />
				<Box component="img" src="/nurse.png" alt="Nurse icon" sx={{ width: { xs: 40, sm: 50 } }} />
			</Box>

			{/* Card */}
			<Box
				sx={{
					width: "min(500px, calc(100% - 32px))",
					bgcolor: SURFACE,
					borderRadius: 3,
					boxShadow: `0 2px 12px ${alpha(INK_BLACK, 0.08)}`,
					mt: { xs: -2, sm: -3 },
					mb: { xs: 2, sm: 4 },
					px: { xs: 2.5, sm: 4 },
					py: { xs: 2.5, sm: 3 },
					display: "flex",
					flexDirection: "column",
					gap: { xs: 0.5, sm: 0.5 },
				}}
			>
				<Typography sx={{ color: BRAND_SECONDARY, fontWeight: 700, fontSize: { xs: "18px", sm: "22px" }, lineHeight: 1.3 }}>
					{t("welcome.title", { facilityName })}
				</Typography>

				<Typography sx={{ color: BRAND_SECONDARY, fontWeight: 600, fontSize: { xs: "14px", sm: "16px" }, mt: { xs: 0.5, sm: 1 } }}>
					{t("welcome.greeting", { firstName })}
				</Typography>

				<Typography sx={{ color: BRAND_SECONDARY, fontSize: { xs: "12px", sm: "14px" }, lineHeight: 1.5 }}>
					<Trans i18nKey="welcome.description" values={{ facilityName }} components={{ strong: <strong /> }} />
				</Typography>

				<Typography sx={{ color: BRAND_SECONDARY, fontWeight: 600, fontSize: { xs: "12px", sm: "14px" }, mt: { xs: 0.5, sm: 1 } }}>
					{t("welcome.get.started")}
				</Typography>

				<Box component="ul" sx={{ color: BRAND_SECONDARY, fontSize: { xs: "12px", sm: "14px" }, m: 0, pl: 2.5, "& li": { mb: 0.5 } }}>
					<li>{t("welcome.username.info")}</li>
					<li>{t("welcome.download.info")}</li>
				</Box>

				{/* Primary CTA - App store links */}
				<Box
					sx={{
						mt: { xs: 1.5, sm: 2 },
						pt: { xs: 1.5, sm: 2 },
						borderTop: `1px solid ${NEUTRAL[200]}`,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: { xs: 1, sm: 1.5 },
					}}
				>
					<Typography sx={{ color: BRAND_SECONDARY, fontWeight: 600, fontSize: { xs: "13px", sm: "15px" } }}>{t("welcome.download.app")}</Typography>
					<Box sx={{ display: "flex", justifyContent: "center", gap: { xs: 1, sm: 1.5 }, flexWrap: "wrap" }}>
						<Link href={PLAY_STORE_URL} target="_blank" rel="noopener">
							<Box
								component="img"
								src="/google-play-store.png"
								alt="Google Play Store"
								sx={{ width: { xs: 120, sm: 140 }, height: "auto", maxHeight: { xs: 40, sm: 47 } }}
							/>
						</Link>
						<Link href={APP_STORE_URL} target="_blank" rel="noopener">
							<Box
								component="img"
								src="/apple-app-store.png"
								alt="Apple App Store"
								sx={{ width: { xs: 120, sm: 140 }, height: "auto", maxHeight: { xs: 40, sm: 47 } }}
							/>
						</Link>
					</Box>
				</Box>

				{/* Secondary CTA - Set up password */}
				<Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 0.5, sm: 1 }, gap: { xs: 1, sm: 1.5 } }}>
					<Button
						variant="outlined"
						size="small"
						onClick={() => navigate(`/reset-password?token=${encodeURIComponent(token)}&setup=True`)}
						sx={{
							color: BRAND_SECONDARY,
							borderColor: BRAND_SECONDARY,
							textTransform: "none",
							fontSize: { xs: "12px", sm: "13px" },
							px: { xs: 2, sm: 3 },
							py: 0.5,
							"&:hover": { borderColor: BRAND_PRIMARY, color: BRAND_PRIMARY },
						}}
					>
						{t("welcome.setup.password")}
					</Button>
					<Button
						variant="outlined"
						size="small"
						onClick={() => navigate("/login")}
						sx={{
							color: BRAND_SECONDARY,
							borderColor: BRAND_SECONDARY,
							textTransform: "none",
							fontSize: { xs: "12px", sm: "13px" },
							px: { xs: 2, sm: 3 },
							py: 0.5,
							"&:hover": { borderColor: BRAND_PRIMARY, color: BRAND_PRIMARY },
						}}
					>
						{t("sign.in")}
					</Button>
				</Box>

				{/* Footer text */}
				<Box sx={{ mt: { xs: 1.5, sm: 2 }, pt: { xs: 1.5, sm: 2 }, borderTop: `1px solid ${NEUTRAL[200]}` }}>
					<Typography sx={{ color: BRAND_SECONDARY, fontSize: { xs: "11px", sm: "13px" }, lineHeight: 1.5 }}>{t("welcome.support.message")}</Typography>

					<Typography sx={{ color: BRAND_SECONDARY, fontSize: { xs: "11px", sm: "13px" }, mt: { xs: 1, sm: 1.5 } }}>
						{t("welcome.thanks")}
						<br />
						<strong>{t("welcome.team")}</strong>
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};
